// MA-18 — the ONE labeled mastery ladder (single source of truth).
// MA-62 — simplified to a 4-word parent vocabulary (founder preference
//          2026-06-28; see ADR-035).
// MA-296 — REVERSES the MA-62 collapse for `secure` (founder decision
//          MA-DEC-0721 #2; see ADR-093, which supersedes ADR-035 on this one
//          point). Now that MA-295/ADR-086 makes `mastered` a genuinely
//          cross-day-confirmed claim, folding `secure` into the same word was
//          an over-claim: a same-day-only skill read as "Mastered" exactly
//          like one proven to hold a day later. `secure` gets its own honest
//          word again; the tree-position mechanics ADR-035 introduced (Now/
//          Ahead/For younger ages, the fold logic) are untouched.
//
// THE PROBLEM MA-18 KILLS: two parent-facing screens used to disagree on the
// SAME skill. ParentOverview celebrated a skill as "Secured" at the engine's
// SECURE tier (provisionalMastery -> projected score 0.70), while SkillTree
// only showed a "Mastered" check at score >= 0.85 (true/durable) and rendered
// a provisional skill as merely "Practicing" -- AND leaked a bare "0.85" into
// parent copy. So a provisional skill read "Secured" on one screen and
// "still practicing" on the other. (See docs/adr/007-unify-mastery-label.md.)
//
// MA-85 — ADR-107 supersedes ADR-093 on the WORD only: the `secure` tier's
//          displayed word is "Almost mastered" (founder, 2026-10-04: "what is
//          secure?"). The tier, its threshold and the internal token are
//          unchanged; the two-word distinction stays.
//
// CURRENT LABELS (post-MA-85) --
//   "Almost mastered" for the `secure` tier (>= MASTERY_SCORE_SECURE, same-day)
//   "Mastered" for the `mastered` tier (>= MASTERY_SCORE_MASTERED, cross-day)
//   "Now"      for the currently-active skill (practicing)
//   "Ahead"    and "For younger ages" are tree-position chips (SkillTree JSX)
// The engine still internally distinguishes `secure` from `mastered`, and now
// the DISPLAY label does too. See ADR-093 for the rationale.
//
// HOW THE SCORE MAPS (set by useSkillGraph.masteryScore -- the engine
// projection this ladder is calibrated against; keep the two in lockstep):
//   durableMastery     -> 1.00  -+- "Mastered"   (>= MASTERY_SCORE_MASTERED)
//   trueMastery        -> 0.90  -+
//   provisionalMastery -> 0.70  --- "Almost mastered" (>= MASTERY_SCORE_SECURE)
//   readinessOnly      -> 0.40  -+
//   accuracy-derived   -> <=0.60 -+- "Now"       (> 0, below Almost mastered)
//   no attempts        -> null/0 --- "none"        (not started)
//
// MASTERY_SCORE_SECURE is exactly the projected provisionalMastery score, so
// the Overview's provisional count and the tree's "Almost mastered" badge land
// on the SAME boundary by construction (overviewModel.isSkillMastered counts
// provisional-or-higher == score >= MASTERY_SCORE_SECURE).

/** The honest "Mastered" bar: retention + transfer proven (true/durable). */
export const MASTERY_SCORE_MASTERED = 0.85;

/** The "Almost mastered" bar (the `secure` tier): accurate + independent over
 *  the recent window (provisionalMastery). Equals the projected
 *  provisionalMastery score so the parent Overview's count and the SkillTree's
 *  "Almost mastered" badge agree. */
export const MASTERY_SCORE_SECURE = 0.7;

/** Ordered parent-facing mastery tier. "none" = no attempts yet (not started),
 *  rendered as no badge rather than a label. */
type MasteryTier = "mastered" | "secure" | "practicing" | "none";

/** The ONE banding function. Pure; the single place a projected mastery_score
 *  becomes a parent-facing tier. Boundaries are INCLUSIVE at the threshold
 *  (0.70 -> secure, 0.85 -> mastered) -- call this everywhere instead of an
 *  inline `>=` so a surface can never drift inclusive/exclusive. A null/absent
 *  score (no attempts) is "none". */
export function masteryTier(score: number | null | undefined): MasteryTier {
  const m = typeof score === "number" && Number.isFinite(score) ? score : 0;
  if (m >= MASTERY_SCORE_MASTERED) return "mastered";
  if (m >= MASTERY_SCORE_SECURE) return "secure";
  if (m > 0) return "practicing";
  return "none";
}

/** The ONE parent-facing word per tier. "none" has no badge.
 *  MA-296: `secure` and `mastered` are distinct words again — `secure` is a
 *  same-day, provisional claim; `mastered` is cross-day-confirmed (MA-295's
 *  `confirmedSecure` gate). MA-841: the words follow the tree's one progress
 *  scale — `secure` reads "Almost there", `practicing` reads "Practising". */
export const MASTERY_TIER_LABEL: Record<MasteryTier, string> = {
  mastered: "Mastered",
  secure: "Almost there",
  practicing: "Practising",
  none: "",
};

/** Plain-English, parent-friendly gloss per tier -- used in help/disclosure
 *  copy so a parent understands the distinction without ever seeing a raw
 *  score. No raw numeric scores; no forbidden label words ("Practicing").
 *  MA-841 (operator D-1008-2) deliberately re-admits the word in its UK
 *  spelling, "Practising", as one of the five scale words (ADR-007's ban was
 *  on the old ladder's "Practicing" tier label). */
export const MASTERY_TIER_GLOSS: Record<MasteryTier, string> = {
  mastered: "Mastered — still right on a later day.",
  secure: "Almost there — right on their own; not yet checked on a later day.",
  practicing: "Practising — currently working on this skill.",
  none: "Not started yet.",
};
